import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { PrecisionsDataContext } from "@shared/features/common/PrecisionsData.context";
import {
  selectDisplayCurrency,
  selectPrecisionsData,
} from "@shared/slices/common.slice";
import { sharedLib } from "@shared/services/shared.service";
import {
  CoinPrecisionConfig,
  DisplayCurrency,
  PrecisionType,
} from "@shared/types";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { useSelector } from "react-redux";
import { isNumber } from "lodash";
import { smartRound, toFixed } from "@shared/utils/numbers";

const PrecisionsDataProvider = ({ children }: PropsWithChildren) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const data = useSelector(selectPrecisionsData);
  const { isLoading } =
    sharedLib.sharedApi.common.endpoints.getPrecisions.useQueryState(undefined);

  const getPrecision = useCallback(
    (precisionType: PrecisionType, symbol?: string, exchange?: string) => {
      const BASE_PRECISION_TYPES = [
        "btc_amount",
        "default",
        "general_percent",
        "usd_amount",
        "usd_price",
        "btc_price",
      ];
      let precision: number | undefined;
      if (symbol && exchange) {
        const symbolPrecisionConfig = data?.[exchange]?.[symbol];
        if (symbolPrecisionConfig && precisionType in symbolPrecisionConfig) {
          precision =
            symbolPrecisionConfig[precisionType as keyof CoinPrecisionConfig];
        }
      }
      if (
        !isNumber(precision) &&
        data &&
        BASE_PRECISION_TYPES.includes(precisionType)
      ) {
        precision = data[precisionType] as unknown as number;
      }
      return isNumber(precision) ? precision : data?.default || 0;
    },
    [data]
  );

  const getDisplayCurrencyAmountPrecision = useCallback(
    (argsDisplayCurrency?: DisplayCurrency) => {
      const _displayCurrency = argsDisplayCurrency ?? displayCurrency;
      return getPrecision(
        _displayCurrency === DISPLAY_CURRENCIES.BTC
          ? PrecisionType.BTC_AMOUNT
          : PrecisionType.USD_AMOUNT
      );
    },
    [displayCurrency, getPrecision]
  );

  const getMinimumPrecision = (
    precisionType: PrecisionType,
    symbol: string,
    exchanges: string[]
  ) => {
    let minPrecision: number = 0;
    for (let index = 0; index < exchanges.length; index++) {
      const precision = getPrecision(precisionType, symbol, exchanges[index]);
      if (index === 0 || precision < minPrecision) minPrecision = precision;
    }
    return minPrecision;
  };

  const applyPrecision = useCallback(
    (
      value: number,
      precisionType: PrecisionType,
      symbol?: string,
      exchange?: string
    ) => {
      if (precisionType === PrecisionType.AMOUNT) return smartRound(value);
      else return toFixed(value, getPrecision(precisionType, symbol, exchange));
    },
    [getPrecision]
  );

  const value = useMemo(() => {
    return {
      data,
      getPrecision,
      getMinimumPrecision,
      isLoading,
      getDisplayCurrencyAmountPrecision,
      applyPrecision,
    };
  }, [data, getDisplayCurrencyAmountPrecision, isLoading]);

  return (
    <PrecisionsDataContext.Provider value={value}>
      {children}
    </PrecisionsDataContext.Provider>
  );
};

export default PrecisionsDataProvider;
